import { Component, effect, inject, OnInit, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, ReactiveFormsModule } from '@angular/forms';
import { MedicalReportCreateStore } from './medical-report-create-dialog.store';
import {
    DiagnosisObject,
    FolderMedicationGridItemObject,
    FolderMedicationGridObject,
    WorkGridItemObject,
    WorkGridObject,
} from '@it2go/types';
import { TranslateService } from '@ngx-translate/core';
import { FilterBuilder } from '../../../helper/filter.builder';
import { WorkDataDoctorRecordObject } from '@libs/types/src/services/object-type/work/work-doctor-record.object';
import { DatePipe } from '@angular/common';
import { fb, translate } from '../../../../shared/shared.global-service';
import { formatDosing } from '../../../pages/person/folder-medications-page/folder-medications.pipe';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import { changeSignal, formAddText } from '../../../../../utils/form.utils';
import { FolderMedicalReportGridItemObject } from '@libs/types/src/services/object-type/folder/folder-medical-report.object';
import { dateify } from '../../../../shared/helpers/date.helper';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { InputWysiwygComponent } from '../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { medicationFrag } from './medical-report-create-dialog.query';
import { contentGlobalSuccess } from '../../../content.error';
import { BaseServicePrintDialogComponent } from 'packages/client/src/app/components/medical-service/print/base-service-print-dialog/base-service-print-dialog.component';
import { BeforePrintDialogResponse } from '../../common/before-print-dialog/before-print-dialog.component';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { DiagnosisSelectInputComponent } from '../../../../../components/medical-service/diagnosis-select-input/diagnosis-select-input.component';
import { CancelButtonComponent } from '../../../../../components/kit/button/cancel-button.component';
import { SaveButtonComponent } from '../../../../../components/kit/button/save-button.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import {
    BackgroundActionType,
    ClientDocumentExportKind,
} from '@libs/types/src/notifications/types/background-action.types';

export interface MedicalReportDialogUrlQuery {
    dateFrom: string | null | undefined;
    dateTo: string | null | undefined;
    open: 'true' | 'false' | null | undefined;
}

@Component({
    selector: 'app-medical-report-create-dialog',
    templateUrl: './medical-report-create-dialog.component.html',
    styleUrl: './medical-report-create-dialog.component.scss',
    providers: [MedicalReportCreateStore, DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        DateInputComponent,
        CheckInputComponent,
        InputWysiwygComponent,
        DatePipe,
        DiagnosisSelectInputComponent,
        CancelButtonComponent,
        SaveButtonComponent,
        ButtonComponent,
    ],
})
export class MedicalReportCreateDialogComponent
    extends BaseDialogComponent
    implements OnInit
{
    protected readonly store = inject(MedicalReportCreateStore);
    protected readonly translateService = inject(TranslateService);
    protected readonly datePipe = inject(DatePipe);

    protected medicalReport: FolderMedicalReportGridItemObject | null;
    protected dateFrom: Date | null = null;
    protected dateTo: Date | null = null;

    public readonly dialogService = inject(BaseDialogService);
    private readonly backgroundTaskLauncher = inject(
        BackgroundTaskLaunchService,
    );

    public readonly wysiwyg = viewChild<InputWysiwygComponent>(
        InputWysiwygComponent,
    );

    protected form = fb.group({
        dateFrom: fb.control<Date | null>(null),
        dateTo: fb.control<Date | null>(null),
        diagnoses: fb.control<string[] | null>([]),
        content: fb.control<string>('', { nonNullable: true }),
        anamnesis: fb.control<boolean>(false),
        diagnose: fb.control<boolean>(false),
        bloodType: fb.control<boolean>(false),
        medication: fb.control<boolean>(false),
        allergy: fb.control<boolean>(false),
        dekurs: fb.control<boolean>(false),
        dekurses: fb.array<any[] | null>([]),
    });

    protected prefix = 'folder.medicalReports.createDialog.';

    protected processing: boolean = false;

    protected works: WorkGridItemObject[] = [];

    private dateFromSignal = toSignal(this.form.controls.dateFrom.valueChanges);
    private dateToSignal = changeSignal(this.form.controls.dateTo);
    private diagnosesSignal = changeSignal(this.form.controls.diagnoses);

    private medicationSignal = changeSignal(this.form.controls.medication);
    private anamnesisSignal = changeSignal(this.form.controls.anamnesis);
    private diagnoseSignal = changeSignal(this.form.controls.diagnose);
    private allergySignal = changeSignal(this.form.controls.allergy);
    private bloodTypeSignal = changeSignal(this.form.controls.bloodType);
    private dekursSignal = changeSignal(this.form.controls.dekurs);
    private dekursesSignal = toSignal(
        this.form.controls.dekurses.valueChanges.pipe(
            startWith(this.form.controls.dekurses.value),
        ),
    );

    constructor() {
        super();

        this.medicalReport = this.dialogConfig.data.item;
        this.dateFrom = this.dialogConfig.data.dateFrom;
        this.dateTo = this.dialogConfig.data.dateTo;

        this.form.patchValue({
            content: this.medicalReport?.content || '',
            dateFrom: this.dateFrom
                ? this.dateFrom
                : dateify(this.medicalReport?.createdAt),
            dateTo: this.dateTo,
            anamnesis: this.medicalReport?.imported?.anamnesis ?? false,
            diagnose: this.medicalReport?.imported?.diagnose ?? false,
            bloodType: this.medicalReport?.imported?.bloodType ?? false,
            medication: this.medicalReport?.imported?.medication ?? false,
            allergy: this.medicalReport?.imported?.allergy ?? false,
        });

        this.store.api
            .getAnamneses(
                FilterBuilder.singlePage({
                    folderId: this.store.folderId,
                    sort: { column: 'updatedAt', direction: 'DESC' },
                }),
            )
            .then();

        effect(() => {
            const dateFrom = this.dateFromSignal();
            const dateTo = this.dateToSignal();
            const diagnoses = this.diagnosesSignal();
            const filter = new FilterBuilder()
                .where('folderId', this.store.folderId)
                .where('workType', WorkTypeEnum.DOCTOR_RECORD);

            if (dateFrom) {
                filter.where('dateFrom', dateFrom.toISOString(), 'GTE');
            }
            if (dateTo) {
                filter.where('dateTo', dateTo.toISOString(), 'LTE');
            }
            if (diagnoses) {
                filter.where('diagnosisCode', diagnoses, 'IN');
            }
            this.store.api
                .getWorks(filter.create())
                .then((works: WorkGridObject) => {
                    this.works = works?.items || [];

                    this.createDekurses(false);
                });
        });

        effect(async () => {
            if (this.medicationSignal()) {
                await this.handleMedication();
            }
        });

        effect(() => {
            if (this.anamnesisSignal()) {
                this.handleString(
                    this.translateService.instant(
                        `${this.prefix}fields.anamnesis`,
                    ),
                    this.store.anamneses()?.[0]?.anamnesis,
                );
            }
        });

        effect(() => {
            if (this.diagnoseSignal()) {
                this.handleDiagnose();
            }
        });

        effect(() => {
            if (this.allergySignal()) {
                this.handleString(
                    this.translateService.instant(
                        `${this.prefix}fields.allergy`,
                    ),
                    this.store.anamneses()?.[0]?.alergies,
                );
            }
        });

        effect(() => {
            if (this.bloodTypeSignal()) {
                this.handleBloodType();
            }
        });

        effect(() => {
            this.createDekurses(this.dekursSignal() || false);
        });

        effect(() => {
            const dekurses = this.dekursesSignal();

            dekurses
                ?.filter((dekurs: any) => dekurs.selected)
                .forEach((dekurs: any) => {
                    if (dekurs.added) {
                        return;
                    }
                    this.handleString(
                        this.translateService.instant(
                            `${this.prefix}fields.decurs`,
                        ) + ` ${this.datePipe.transform(dekurs.date, 'd.M.y')}`,
                        dekurs.note,
                    );
                    dekurs.added = true;
                });
        });
    }

    ngOnInit(): void {
        if (!this.store.isAllowed('doctorWork.create')()) {
            this.form.disable();
        }
    }

    protected print(): void {
        if (this.form.valid) {
            this.export();
        }
    }

    public override async save(): Promise<void> {
        if (this.form.invalid) {
            return;
        }

        this.processing = true;
        try {
            await this.store.api.createFolderMedicalReport({
                folderId: this.store.folderId,
                content: this.form.controls.content.value,
                imported: this.getImported(),
                archive: false,
                print: false,
            });
            this.finishCreation();
        } finally {
            this.processing = false;
        }
    }

    private async handleMedication() {
        this.store.api
            .getFolderMedications(
                {
                    filter: [
                        [
                            {
                                column: 'folderId',
                                operator: 'EQ',
                                value: [this.store.folderId.toString()],
                            },
                        ],
                        [
                            {
                                column: 'from',
                                operator: 'LTE',
                                value: [new Date().toISOString()],
                            },
                        ],
                        [
                            {
                                column: 'to',
                                operator: 'GTE',
                                value: [new Date().toISOString()],
                            },
                        ],
                        [{ column: 'active', operator: 'EQ', value: ['true'] }],
                    ],
                },
                medicationFrag,
            )
            .then((medications: FolderMedicationGridObject) => {
                if (!medications.items.length) return;

                const medHtml = `
                <div id="medication">
                    <p><b>${this.translateService.instant(`${this.prefix}fields.medication`)}:</b></p>
                    ${medications?.items
                        .map(
                            (med: FolderMedicationGridItemObject) => `
                        <p>${med.customMedicationName.trim() || med.medicationName.trim() || med.medicationCode.trim()}: <i>${formatDosing(
                            med.dosingPeriod,
                            med.dosing,
                            this.store.globalGlobalStore.currentService()
                                ?.timeRanges,
                        )}</i></p>
                    `,
                        )
                        .join('')}
                </div>
            `;

                formAddText(this.form, 'content', medHtml);
            });
    }

    private handleString(header: string, text?: string): void {
        if (!text) {
            return;
        }

        const html = `
            <div id="${header}">
                <p><b>${header}:</b></p>
                <p>${text}</p>
            </div>
        `;
        formAddText(this.form, 'content', html);
    }

    private handleDiagnose() {
        const anamnesis = this.store.anamneses()?.[0];
        if (!anamnesis?.diagnosis?.code) return;
        const html = `
            <div id="diagnoses">
                <p><b>${this.translateService.instant(`${this.prefix}fields.diagnoses`)}:</b></p>
                <p>${anamnesis.diagnosis.code} ${anamnesis.diagnosis.name}</p>
                ${anamnesis.secondaryDiagnoses
                    .map(
                        (diagnose: DiagnosisObject) => `
                    <p>${diagnose.code} ${diagnose.name}</p>
                `,
                    )
                    .join('')}
            </div>
        `;
        formAddText(this.form, 'content', html);
    }

    private handleBloodType() {
        if (!this.store.anamneses()?.[0]?.bloodType) return;

        const bloodType = this.translateService.instant(
            'enum.bloodType.' + this.store.anamneses()?.[0]?.bloodType,
        );
        const html = `
            <div id="bloodType">
                <p><b>${this.translateService.instant(`${this.prefix}fields.bloodType`)}:</b></p>
                <p>${bloodType}</p>
            </div>
        `;
        formAddText(this.form, 'content', html);
    }

    private createDekurses(selected: boolean) {
        const dekurses = this.form.get('dekurses') as FormArray;
        dekurses.clear();

        this.works.forEach((item: WorkGridItemObject) => {
            const wdr = item.workData as WorkDataDoctorRecordObject;
            const work = fb.group({
                id: item.id,
                note: wdr.note,
                date: wdr.recordTimestamp,
                selected: fb.control<boolean>(!!selected),
                added: false,
            });

            if (!this.store.isAllowed('doctorWork.create')()) {
                work.disable();
            }

            dekurses.push(work);
        });
    }

    private export() {
        const serviceId =
            this.store.globalGlobalStore.loggedUserStore.selectedServiceId();
        this.dialogService.open(BaseServicePrintDialogComponent, {
            header: translate.instant('common.print'),
            data: {
                serviceId,
                onStart: async (value: BeforePrintDialogResponse) => {
                    this.processing = true;
                    try {
                        const started = await this.backgroundTaskLauncher.start(
                            {
                                type: BackgroundActionType.CLIENT_DOCUMENT_EXPORT,
                                payload: {
                                    folderId: this.store.folderId,
                                    kind: ClientDocumentExportKind.MEDICAL_REPORT,
                                    input: {
                                        folderId: this.store.folderId,
                                        content:
                                            this.form.controls.content.value,
                                        imported: this.getImported(),
                                        headerId: value.headerId || 0,
                                        footerId: value.footerId || 0,
                                        archive: value.archive || false,
                                        print: true,
                                        serviceId,
                                    },
                                },
                                alreadyRunningMessage:
                                    'person.documentExport.backgroundTask.messages.alreadyRunning',
                                failedMessage:
                                    'person.documentExport.backgroundTask.messages.startFailed',
                            },
                        );
                        if (started) this.finishCreation();
                        return started;
                    } finally {
                        this.processing = false;
                    }
                },
            },
        });
    }

    private getImported(): string {
        return JSON.stringify({
            anamnesis: this.form.controls.anamnesis.value ?? false,
            diagnose: this.form.controls.diagnose.value ?? false,
            bloodType: this.form.controls.bloodType.value ?? false,
            medication: this.form.controls.medication.value ?? false,
            allergy: this.form.controls.allergy.value ?? false,
        });
    }

    private finishCreation(): void {
        this.wysiwyg()?.resetBackupValues();
        contentGlobalSuccess.next('common.created');
        this.dialogRef.close({ reload: true });
    }
}
